import { EventExtendedViewRow, LivestreamSession, TicketItemWithBookings } from 'cb-model';
import { fetchApi, getConfig, makeResetCache, notify, selectUser, useCacheRecord } from 'gossamer-front-end';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AttendanceProgress } from '../website-other/AttendanceProgress';
import { useLivestreamStatus } from '../livestream/useLivestreamStatus';
import { VideoPlayer } from './VideoPlayer';
import { makeEventUrlSlug } from 'shared-front-end';
import styles from './EventBanner.module.css';

interface EventBannerProps {
  livestreamSessionId: string;
  image: string;
  event: EventExtendedViewRow;
  isEventBooked: boolean;
  isBookedAndMarkedAttendance: boolean;
}

export const EventBanner = ({
  livestreamSessionId,
  image,
  event,
  isEventBooked,
  isBookedAndMarkedAttendance,
}: EventBannerProps) => {
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const recordingStatus = useLivestreamStatus(livestreamSessionId);
  const [watchPercentage, setWatchPercentage] = useState(0);
  const [hasAutoBookedAccess, setHasAutoBookedAccess] = useState(false);
  const [isAutoBooking, setIsAutoBooking] = useState(false);

  const resetCache = makeResetCache(dispatch);
  const tickets = event.ticket_data as unknown as TicketItemWithBookings[];
  const availableTickets = useMemo(() => tickets.filter((ticket) => ticket.quantity - ticket.bookings > 0), [tickets]);
  const isFreeEvent = tickets.length > 0 && tickets.every((ticket) => Number(ticket.price || 0) === 0);
  const firstAvailableFreeTicket = availableTickets.find((ticket) => Number(ticket.price || 0) === 0);
  const hasFullRecordingAccess = isEventBooked || hasAutoBookedAccess;

  const isPaidPreview = recordingStatus.hasVideoRecording && !hasFullRecordingAccess && !isFreeEvent;

  const handlePlayClick = async () => {
    if (!recordingStatus.hasVideoRecording || hasFullRecordingAccess || !isFreeEvent) {
      return true;
    }

    if (!user?.userId) {
      notify(['Sign in to book this event before watching the recording.', 'danger']);
      return false;
    }

    if (!firstAvailableFreeTicket) {
      notify(['No free tickets are currently available for this recording.', 'danger']);
      return false;
    }

    try {
      setIsAutoBooking(true);
      const response = await fetchApi('POST', '/main/book-user-with-ticket', { ticketId: firstAvailableFreeTicket.id });
      if (response.status >= 300) {
        notify([response.error || 'Could not auto-book you onto this event.', 'danger']);
        return false;
      }

      setHasAutoBookedAccess(true);
      resetCache('BookingExtendedView');
      resetCache('BookingView');
      resetCache('EventExtendedView', String(event.id));
      notify(['You have been booked onto this event.', 'success']);
      return true;
    } catch (error) {
      console.error('Auto-booking failed:', error);
      notify(['Could not auto-book you onto this event.', 'danger']);
      return false;
    } finally {
      setIsAutoBooking(false);
    }
  };

  return (
    <div>
      <div className={styles.EventBanner}>
        {recordingStatus.hasVideoRecording ? (
          <VideoPlayer
            onProgressChange={(percentage) => setWatchPercentage(percentage)}
            onPlayClick={handlePlayClick}
            width="auto"
            height="100%"
            maxPlaybackSeconds={isPaidPreview ? 10 : undefined}
            src={`${getConfig('gossamer-be-origin')}/gossamer/main/livestream/recordings/${livestreamSessionId}/stream`}
          />
        ) : (
          <img src={image} alt="Cover image" />
        )}
      </div>
      {recordingStatus.hasVideoRecording && !hasFullRecordingAccess && isFreeEvent && (
        <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">
          Click play to auto-book yourself onto this free event and unlock the full recording.
          {isAutoBooking && <span className="ml-2 font-medium">Booking you now...</span>}
        </div>
      )}
      {isPaidPreview && (
        <div className="mt-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          This is a 10 second preview. You need to book this event to watch the full recording.
        </div>
      )}
      {!isBookedAndMarkedAttendance && recordingStatus.hasVideoRecording && hasFullRecordingAccess && (
        <AttendanceProgress
          eventId={event.id}
          percentWatched={watchPercentage}
          isEligible={watchPercentage >= 80}
          eventSlug={makeEventUrlSlug(event.title || 'event', event.id)}
        />
      )}
    </div>
  );
};
